import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FaComments, FaEnvelope, FaClock, FaUser } from 'react-icons/fa';

export default function RentalConversations() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [user]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/rentals/conversations/list?user_id=${user.id}`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {t('rental.myConversations')}
          </h1>
          <p className="text-gray-600">
            Gérez vos conversations concernant les locations d'équipements
          </p>
        </div>

        {conversations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FaEnvelope className="mx-auto text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">
                Vous n'avez aucune conversation pour le moment
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Les conversations commencent lorsque vous contactez un loueur ou qu'un locataire vous contacte
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {conversations.map((conv) => {
              const isOwner = user.id === conv.owner_id;
              const otherPartyName = isOwner ? conv.renter_name : conv.owner_name;
              const unreadCount = isOwner ? conv.unread_count_owner : conv.unread_count_renter;
              
              return (
                <Card
                  key={conv.id}
                  className="hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/rentals/conversations/${conv.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FaComments className="text-[#00D4FF] text-2xl" />
                          <h3 className="text-xl font-bold text-[#0A2540]">
                            {conv.product_name}
                          </h3>
                          {conv.status === 'active' && (
                            <Badge className="bg-[#635BFF] text-white">Active</Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <FaUser className="text-sm" />
                          <span className="text-sm">
                            {isOwner ? 'Locataire' : 'Loueur'}: <strong>{otherPartyName}</strong>
                          </span>
                        </div>

                        {conv.last_message_preview && (
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {conv.last_message_preview}
                          </p>
                        )}

                        <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                          <FaClock />
                          <span>
                            Dernière activité: {new Date(conv.last_message_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>

                      {unreadCount > 0 && (
                        <Badge className="bg-[#00D4FF] text-white">
                          {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
